import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Popover } from "@mui/material";
import * as React from "react";

import { useAuth } from "@/providers/auth";
import { RobotoTypography } from "@/shared/components";
import { OrgRecord } from "@/shared/domain/orgs";

import styles from "./OrgSelectionMenu.module.css";

interface OrgSelectionMenuProps {
  anchorEl: HTMLElement | null;
  handleClose: () => void;
}

export function OrgSelectionMenu(props: OrgSelectionMenuProps) {
  const {
    availableOrganizations,
    currentOrganization,
    setCurrentOrganization,
  } = useAuth();

  const orgOptions = React.useMemo(() => {
    return toOrgOptions(availableOrganizations ?? [], currentOrganization);
  }, [availableOrganizations, currentOrganization]);

  return (
    <Popover
      open={Boolean(props.anchorEl)}
      anchorEl={props.anchorEl}
      onClose={props.handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: 30,
        horizontal: -215,
      }}
      marginThreshold={5}
    >
      <ul className={styles.menuList}>
        {orgOptions?.map((org) => {
          return (
            <MenuItem
              key={org.id}
              org={org}
              onClick={() => {
                setCurrentOrganization(org.record);
                props.handleClose();
              }}
            />
          );
        })}
      </ul>
    </Popover>
  );
}

function MenuItem({ org, onClick }: { org: OrgOption; onClick: () => void }) {
  return (
    <li>
      <button className={styles.menuItem} onClick={onClick}>
        <RobotoTypography variant="body2">{org.name}</RobotoTypography>
        {org.isActive && (
          <CheckCircleIcon color="success" className={styles.checkmark} />
        )}
      </button>
    </li>
  );
}

interface OrgOption {
  id: string;
  isActive: boolean;
  name: string;
  record: OrgRecord;
}

function toOrgOptions(
  availableOrgs: OrgRecord[],
  currentOrg: OrgRecord | null,
): OrgOption[] {
  return availableOrgs
    .map((org) => ({
      id: org.org_id,
      isActive: org.org_id === currentOrg?.org_id,
      name: org.name,
      record: org,
    }))
    .sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
}
